import {
  Icon,
  IconButton,
  Text,
  Tooltip,
  TooltipButton,
  TooltipAdvancedContent,
} from "@smartsuite/react-ui/lib";
import {
  StyledHelpText,
  StyledIcon,
  StyledTitle,
  StyledTopLine,
  StyledRightOptions,
  StyledTooltipWrapper,
  StyledRemoveButton,
  StyledTopLineOuter,
  StyledTopLineHead,
  StyledOptionLabel,
  StyledParseValues,
  StyledCreateNewValues,
} from "./styles";
import { isTruthy } from "remeda";
import { t } from "i18next";
import type {
  InputDescription,
  SplitType,
} from "@smartsuite/automation-engine-api";
import { isRequired } from "../../containers/AutomationForm/Forms/InputForm/utils";
import { Spinner } from "../Spinner";
import { SanitizedHTML } from "../../utils/sanitizedHtml";
import type React from "react";
import { translateIfPresent } from "../../locales/i18n";
import { ParseValues } from "./ParseValues/ParseValues";
import { GrayColors, SolutionColorVariables } from "@smartsuite/types";
import { CreateNewValues } from "@smartsuite/automation-engine-api";
import { CreateNewValuesComponent } from "./CreateNewValues/CreateNewValues";

export const CaptionBlock: React.FC<{
  description: Pick<
    InputDescription,
    | "icon"
    | "label"
    | "subLabel"
    | "display"
    | "dynamic"
    | "tooltip"
    | "labelSuffix"
    | "groupFieldId"
    | "createNewValuesSupport"
  >;
  onClickTrash?: () => void;
  interval?: boolean;
  largeCaption?: boolean;
  isOperatorSelect?: boolean;
  splitType?: SplitType;
  createNewValues?: CreateNewValues;
  onSplitChange?: (splitType: SplitType) => void;
  onCreateNewValuesChange?: (supportNewValues: CreateNewValues) => void;
}> = ({
  description: {
    icon,
    label,
    subLabel,
    display,
    dynamic,
    tooltip,
    labelSuffix,
    groupFieldId,
    createNewValuesSupport,
  },
  onClickTrash,
  interval,
  largeCaption,
  isOperatorSelect = false,
  splitType,
  createNewValues,
  onSplitChange,
  onCreateNewValuesChange,
  children,
}) => {
  const isTrashEnabled = (): boolean =>
    display?.display.oneofKind === "group" &&
    display?.display.group.groupId === "ss-fields-group";

  const isEntireField = labelSuffix === "Entire Field";
  const labelText =
    labelSuffix && !isEntireField ? `${label}: ${labelSuffix}` : label;

  return (
    <StyledTopLineOuter>
      <StyledTopLine>
        <StyledTopLineHead>
          {icon !== undefined && icon.length > 0 && (
            <StyledIcon>
              <Icon name={icon} size="S" color={SolutionColorVariables.sc1} />
            </StyledIcon>
          )}
          <StyledTitle
            largeCaption={largeCaption}
            className="caption-block-title"
          >
            {translateIfPresent(`caption.${labelText}`, labelText)}
            {groupFieldId && (!labelSuffix || isEntireField) && (
              <StyledOptionLabel> • Entire Field</StyledOptionLabel>
            )}
          </StyledTitle>

          {isOperatorSelect && children}

          {isRequired(display) && (
            <Text
              size="s"
              weight="semibold"
              color="error"
              styles={{ marginLeft: "2px", lineHeight: "11px" }}
            >
              *
            </Text>
          )}
          {tooltip && (
            <StyledTooltipWrapper>
              <Tooltip
                display="inline-flex"
                maxWidth={360}
                padding={2}
                placements={["right"]}
                text={tooltip.text}
                target={
                  <TooltipButton
                    iconSize="S"
                    iconName="info-filled"
                    colors={{
                      hover: GrayColors["G-7"],
                    }}
                  />
                }
                renderTooltipContent={
                  <TooltipAdvancedContent
                    minWidth={340}
                    imageWidth={340}
                    imageHeight={220}
                    paddingType="uniform"
                    imageUrl={tooltip.iconUrl}
                    text={translateIfPresent(
                      `caption.${tooltip.text}`,
                      tooltip.text,
                    )}
                    textSize="xs"
                    textWeight="semibold"
                  />
                }
              />
            </StyledTooltipWrapper>
          )}
          {dynamic && <Spinner />}
        </StyledTopLineHead>
        <StyledRightOptions>
          {isTruthy(interval) && (
            <Text
              size="xxxs"
              weight="bold"
              transformMode="uppercase"
              color="secondary"
              className="add-interval-button"
            >
              {t("date-editor.interval")}
            </Text>
          )}
          {createNewValuesSupport === CreateNewValues.YES &&
            onCreateNewValuesChange !== undefined && (
              <StyledCreateNewValues>
                <CreateNewValuesComponent
                  onChange={onCreateNewValuesChange ?? (() => {})}
                  displayPipe={
                    !(onSplitChange !== undefined && splitType !== undefined)
                  }
                  createNewValues={createNewValues ?? CreateNewValues.NO}
                />
              </StyledCreateNewValues>
            )}
          {onSplitChange !== undefined && splitType !== undefined && (
            <StyledParseValues>
              <ParseValues splitType={splitType} onChange={onSplitChange} />
            </StyledParseValues>
          )}
          {isTruthy(onClickTrash) && isTrashEnabled() && (
            <StyledRemoveButton>
              <IconButton
                name="trash-duotone"
                size="S"
                ariaLabel="Remove Button"
                color="currentColor"
                hoverColor="currentColor"
                onClick={() => {
                  onClickTrash();
                }}
              />
            </StyledRemoveButton>
          )}
        </StyledRightOptions>
      </StyledTopLine>
      {subLabel !== "" && (
        <StyledHelpText>
          <Text size={largeCaption ? "m" : "xs"} color="secondary">
            <SanitizedHTML
              html={translateIfPresent(`caption.${subLabel}`, subLabel)}
              options={{
                allowedTags: ["span", "p", "b", "a"],
                allowedAttributes: {
                  a: ["href", "target"],
                  span: ["style"],
                },
              }}
            />
          </Text>
        </StyledHelpText>
      )}
    </StyledTopLineOuter>
  );
};
